import r from '../Utils/fetch';

const w: any = window;
const endPoint = `${w.API_CORE_URL}`;

// Flux d'activité — surface ADMIN (tout le parc). Même read model et même contrat
// que l'espace partenaire : api-core renvoie du STRUCTURÉ, l'interprétation (phrase
// affichée) est faite ici. Cf. .claude/plans/client-activity.md
//
// Côté admin, billingAccountId/projectFolderId RÉTRÉCISSENT un périmètre déjà global :
// ce sont des filtres de lecture, pas une frontière de sécurité.

export interface ActivityItem {
	type: string;
	occurredAt: string;
	actor: { userId?: number; label?: string };
	object: { type?: string; id?: string; label?: string };
	scope: {
		projectFolderId?: string;
		projectName?: string;
		// projectId = clé primaire de qm_projects, distincte du folderId. C'est elle
		// qu'attend la route /projects/:id de ui-admin.
		projectId?: number;
		billingAccountId?: string;
		billingAccountName?: string;
	};
	isMachine: boolean;
	fallbackText: string;
}

export interface ActivityMetrics {
	lastSeenAt: string | null;
	l7: number;
	l28: number;
	trendL7: number;
	breadth: number;
}

export interface ActivityResponse {
	feed: { items: ActivityItem[]; total: number };
	metrics: ActivityMetrics;
}

export interface ActivityFilters {
	days: number;
	billingAccountId?: string;
	projectFolderId?: string;
	type?: string;
	// Filtres d'ENTITÉ — « qui / sur quoi », pour les encarts greffés sur une fiche.
	// actorUserId : filtre par AUTEUR. Axe distinct du périmètre — « ce que cette
	// personne a fait » traverse projets et comptes de facturation.
	actorUserId?: number;
	// objectType/objectId/accountId : filtre par OBJET de l'action (fiche connecteur).
	// ⚠️ accountId est INDISPENSABLE avec objectId : un même SKU peut avoir plusieurs
	// comptes dans un projet, et sans lui l'encart d'un compte montrerait l'activité
	// des autres.
	objectType?: string;
	objectId?: string;
	accountId?: string;
	limit?: number;
	offset?: number;
}

const EMPTY: ActivityResponse = {
	feed: { items: [], total: 0 },
	metrics: { lastSeenAt: null, l7: 0, l28: 0, trendL7: 0, breadth: 0 },
};

// #region normalize
// Ne jamais faire confiance à la forme reçue : une réponse au corps vide donnait
// `feed` undefined et faisait planter le rendu (bug constaté côté partenaire).
const normalize = (res?: Partial<ActivityResponse> | null): ActivityResponse => ({
	feed: {
		items: Array.isArray(res?.feed?.items) ? (res!.feed!.items as ActivityItem[]) : [],
		total: typeof res?.feed?.total === 'number' ? res!.feed!.total : 0,
	},
	metrics: {
		lastSeenAt: res?.metrics?.lastSeenAt ?? null,
		l7: res?.metrics?.l7 ?? 0,
		l28: res?.metrics?.l28 ?? 0,
		trendL7: res?.metrics?.trendL7 ?? 0,
		breadth: res?.metrics?.breadth ?? 0,
	},
});
// #endregion

// #region getActivity
export const getActivity = async (filters: ActivityFilters): Promise<ActivityResponse> => {
	const params = new URLSearchParams();
	params.set('days', String(filters.days));
	if (filters.limit) params.set('limit', String(filters.limit));
	if (filters.offset) params.set('offset', String(filters.offset));
	if (filters.billingAccountId) params.set('billingAccountId', filters.billingAccountId);
	if (filters.projectFolderId) params.set('projectFolderId', filters.projectFolderId);
	if (filters.actorUserId) params.set('actorUserId', String(filters.actorUserId));
	if (filters.objectType) params.set('objectType', filters.objectType);
	if (filters.objectId) params.set('objectId', filters.objectId);
	if (filters.accountId) params.set('accountId', filters.accountId);
	// Répété : le back lit une liste (c.QueryArray("type")).
	if (filters.type) params.append('type', filters.type);

	try {
		const res = await r<Partial<ActivityResponse>>({
			url: `${endPoint}/v1/admin/activity?${params.toString()}`,
			method: 'GET',
		});
		return normalize(res);
	} catch (e) {
		return EMPTY;
	}
};
// #endregion
